import type { ReactNode } from 'react';

import { Box, Button, Group } from '@chakra-ui/react';
import { useMemo } from 'react';
import { FiArrowUp } from 'react-icons/fi';

import {
  MessageMarkdownEditor,
  type MessageMarkdownEditorProps,
} from './message-markdown-editor';

export interface MessageMarkdownComposerRowProps
  extends Pick<
    MessageMarkdownEditorProps,
    | 'value'
    | 'onChange'
    | 'onSubmit'
    | 'placeholder'
    | 'disabled'
    | 'submitOnEnter'
    | 'rootRef'
  > {
  loading?: boolean;
  trailingActions?: ReactNode;
  modelPicker?: ReactNode;
  submitDisabled?: boolean;
  submitAriaLabel?: string;
  submitTitle?: string;
  submitIcon?: ReactNode;
}

export function MessageMarkdownComposerRow({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled = false,
  loading = false,
  submitOnEnter = true,
  rootRef,
  trailingActions,
  modelPicker,
  submitDisabled,
  submitAriaLabel,
  submitTitle,
  submitIcon,
}: MessageMarkdownComposerRowProps) {
  const gridTemplateColumns = useMemo(() => {
    const parts = ['1fr'];
    if (trailingActions) parts.push('auto');

    if (modelPicker) parts.push('auto');

    parts.push('auto');
    return parts.join(' ');
  }, [modelPicker, trailingActions]);

  const buttonDisabled =
    submitDisabled !== undefined
      ? submitDisabled || disabled || loading
      : !value.trim() || disabled || loading;

  const aria = submitAriaLabel ?? 'Send';
  const title = submitTitle ?? submitAriaLabel ?? 'Send';

  return (
    <Box
      width="100%"
      minW={0}
      borderRadius="var(--radius)"
      overflow="hidden"
      boxShadow="var(--shadow-card)"
    >
      <Group
        attached
        width="100%"
        minW={0}
        display="grid"
        gridTemplateColumns={gridTemplateColumns}
        alignItems="stretch"
      >
        <Box
          bg="var(--input-base-background-color)"
          borderColor="var(--border-base-color)"
          borderWidth="var(--border-base-width)"
          minW={0}
        >
          <MessageMarkdownEditor
            value={value}
            onChange={onChange}
            onSubmit={onSubmit}
            placeholder={placeholder}
            disabled={disabled}
            submitOnEnter={submitOnEnter}
            rootRef={rootRef}
            ariaLabel={aria}
          />
        </Box>
        {trailingActions}
        {modelPicker}
        <Button
          type="button"
          size="sm"
          colorScheme="blue"
          variant="solid"
          onClick={() => {
            if (!onSubmit) {
              return;
            }

            void onSubmit();
          }}
          disabled={buttonDisabled}
          loading={loading}
          minW="44px"
          px={3}
          py={0}
          aria-label={aria}
          title={title}
          lineHeight={1}
          style={{
            height: '100%',
            minHeight: '100%',
            alignSelf: 'stretch',
          }}
        >
          {submitIcon ?? <FiArrowUp size={18} strokeWidth={2.5} aria-hidden />}
        </Button>
      </Group>
    </Box>
  );
}
